import { useEffect, useState } from "react";
import { useTranslation } from 'react-i18next';
import axios from "axios";
import { useMsal, useAccount, AuthenticatedTemplate } from "@azure/msal-react";

import { FaHistory } from 'react-icons/fa';
import { format } from 'date-fns';

import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import InputGroup from 'react-bootstrap/InputGroup';
import Pagination from 'react-bootstrap/Pagination';

import authConfig from "../../authConfig";

// Staff-only change log for a meeting: shows who changed what and when.
function MeetingChangeLog(props) {
    const { t } = useTranslation();
    const { instance, accounts } = useMsal();
    const account = useAccount(accounts[0] || {});

    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);
    const [logs, setLogs] = useState([]);
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState(null);

    // Filters
    const [searchText, setSearchText] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [userFilter, setUserFilter] = useState("");

    // Paging
    const pageSizeOptions = [10, 25, 50, 100];
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    // Reset everything whenever the modal is opened.
    useEffect(() => {
        if (show) {
            setSearchText("");
            setDebouncedSearch("");
            setUserFilter("");
            setPageSize(10);
            setPage(1);
        }
    }, [show]);

    // Debounce the free-text search so we don't hit the API on every keystroke.
    useEffect(() => {
        const handle = setTimeout(() => setDebouncedSearch(searchText), 300);
        return () => clearTimeout(handle);
    }, [searchText]);

    // Go back to the first page whenever the filters or page size change.
    useEffect(() => {
        setPage(1);
    }, [debouncedSearch, userFilter, pageSize]);

    // Fetch the current page from the API.
    useEffect(() => {
        if (!show || !props.meetingId || !account) return;
        setLoading(true);
        setError(null);
        instance
            .acquireTokenSilent({
                scopes: authConfig.api.scopes,
                account: account
            })
            .then(response => {
                axios
                    .get(authConfig.api.urls.getMeetingChangeLog + "/" + props.meetingId + "/changelog", {
                        headers: { Authorization: `Bearer ${response.accessToken}` },
                        params: {
                            page: page,
                            pageSize: pageSize,
                            search: debouncedSearch || undefined,
                            user: userFilter || undefined
                        }
                    })
                    .then(res => {
                        setLogs(res.data.entities || []);
                        setTotal(res.data.total || 0);
                        // Keep the user list stable (server returns full distinct set).
                        if (res.data.users) setUsers(res.data.users);
                    })
                    .catch(err => {
                        setError("Error loading change log: " + err.message);
                        setLogs([]);
                        setTotal(0);
                    })
                    .then(() => setLoading(false));
            })
            .catch(() => {
                setError("Could not acquire token.");
                setLoading(false);
            });
    }, [show, props.meetingId, account, instance, page, pageSize, debouncedSearch, userFilter]);

    function formatDate(date) {
        if (!date) return "";
        try {
            return format(new Date(date), "dd/MM/yyyy HH:mm");
        } catch {
            return date;
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const currentPage = Math.min(page, totalPages);
    const hasFilter = debouncedSearch.trim() !== "" || userFilter !== "";

    return (
        <AuthenticatedTemplate>
            <Button variant="outline-secondary" size="sm" onClick={handleShow} title="Change log">
                <FaHistory />&nbsp;{t("Change log", "Change log")}
            </Button>

            <Modal show={show} onHide={handleClose} size="lg" scrollable>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Change log", "Change log")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error ? (
                        <div className="text-danger">{error}</div>
                    ) : (
                        <>
                            <Row className="mb-2 g-2">
                                <Col md={6}>
                                    <InputGroup size="sm">
                                        <InputGroup.Text>{t("Search", "Search")}</InputGroup.Text>
                                        <Form.Control
                                            type="text"
                                            placeholder={t("Search field, value or user...", "Search field, value or user...")}
                                            value={searchText}
                                            onChange={e => setSearchText(e.target.value)}
                                        />
                                        {searchText && (
                                            <Button variant="outline-secondary" onClick={() => setSearchText("")}>
                                                &times;
                                            </Button>
                                        )}
                                    </InputGroup>
                                </Col>
                                <Col md={4}>
                                    <Form.Select
                                        size="sm"
                                        value={userFilter}
                                        onChange={e => setUserFilter(e.target.value)}
                                    >
                                        <option value="">{t("All users", "All users")}</option>
                                        {users.map(u => (
                                            <option key={u} value={u}>{u}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                                <Col md={2}>
                                    <Form.Select
                                        size="sm"
                                        value={pageSize}
                                        onChange={e => setPageSize(Number(e.target.value))}
                                        title={t("Rows per page", "Rows per page")}
                                    >
                                        {pageSizeOptions.map(o => (
                                            <option key={o} value={o}>{o} / {t("page", "page")}</option>
                                        ))}
                                    </Form.Select>
                                </Col>
                            </Row>
                            {loading ? (
                                <div className="text-center my-3">
                                    <Spinner animation="border" role="status" />
                                </div>
                            ) : total === 0 ? (
                                <div>
                                    {hasFilter
                                        ? t("No changes match the filter.", "No changes match the filter.")
                                        : t("No changes recorded.", "No changes recorded.")}
                                </div>
                            ) : (
                                <>
                                    <Table striped bordered hover size="sm">
                                        <thead>
                                            <tr>
                                                <th>{t("When", "When")}</th>
                                                <th>{t("Who", "Who")}</th>
                                                <th>{t("Field", "Field")}</th>
                                                <th>{t("From", "From")}</th>
                                                <th>{t("To", "To")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {logs.map(log => (
                                                <tr key={log.id}>
                                                    <td>{formatDate(log.changedOn)}</td>
                                                    <td>{log.changedBy}</td>
                                                    <td>{log.field}</td>
                                                    <td>{log.oldValue}</td>
                                                    <td>{log.newValue}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    {totalPages > 1 && (
                                        <Pagination size="sm" className="mb-1 justify-content-center">
                                            <Pagination.First disabled={currentPage === 1} onClick={() => setPage(1)} />
                                            <Pagination.Prev disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} />
                                            <Pagination.Item active>{currentPage} / {totalPages}</Pagination.Item>
                                            <Pagination.Next disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} />
                                            <Pagination.Last disabled={currentPage === totalPages} onClick={() => setPage(totalPages)} />
                                        </Pagination>
                                    )}
                                    <div className="text-muted small">
                                        {t("Total", "Total")}: {total}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        {t("Close", "Close")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </AuthenticatedTemplate>
    );
}

export default MeetingChangeLog;
